import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { NavigationDrawer } from './NavigationDrawer'
import { AuthenticatedHeader } from './AuthenticatedHeader'
import { trpc } from '@/lib/trpc'
import { useSyncFeatureFlags } from '@/hooks/useFeatureFlags'
import { useHeaderHeight } from '@/hooks/useChromeLayout'

interface AuthenticatedLayoutProps {
  children: React.ReactNode
}

/**
 * Layout for all authenticated routes
 * Provides drawer navigation and header with eo logo
 */
export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const location = useLocation()
  // Account feature flags follow the login
  useSyncFeatureFlags()

  // Fetch skills for navigation drawer via tRPC
  const { data: skills = [] } = trpc.skills.list.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  })

  // Check if we're on the media viewer page (has custom fullscreen layout)
  const isMediaViewerPage =
    location.pathname.includes('/skills/') && location.pathname.split('/').length >= 4
  // Header shrinks on the media page while the ground-truth bar is collapsed
  const headerHeight = useHeaderHeight(isMediaViewerPage)

  // Close drawer on route change
  useEffect(() => {
    if (isDrawerOpen) {
      setIsDrawerOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  return (
    <>
      {/* Navigation drawer */}
      <NavigationDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false)
        }}
        skills={skills}
      />

      <AuthenticatedHeader
        height={headerHeight}
        onMenuClick={() => {
          setIsDrawerOpen(true)
        }}
        isDrawerOpen={isDrawerOpen}
      />

      {/* Page content with top padding for fixed header - no padding on media viewer */}
      <div
        className={isMediaViewerPage ? '' : 'pt-20'}
        style={isMediaViewerPage ? { margin: 0, padding: 0 } : undefined}
      >
        {children}
      </div>
    </>
  )
}
